import { Datum } from "../../../components/docs/Datum.js";
import { note, p, table, ul } from "../blocks.js";
import { QUOTED } from "../thresholds.js";
import type { WikiTopic } from "../types.js";

export const readingTheFeed: WikiTopic = {
  slug: "reading-the-feed",
  title: "Reading the feed",
  summary: "How the filters compose, which rows double-count, and what an empty family means.",
  group: "reference",
  sections: [
    {
      heading: "Family and Type are one filter wearing two controls",
      code: "?family= & ?signalType=",
      blocks: [
        p(
          <>
            A type belongs to exactly one family, so the two controls can contradict each other.{" "}
            <Datum>?family=rank&signalType=mss_bullish</Datum> matches nothing that can ever exist,
            and the page reports that the same way it reports a calm session. Quiet on screen is
            indistinguishable from a job that died.
          </>,
        ),
        p(
          <>
            The Type dropdown narrows to the chosen family so the contradiction is hard to build.
            Unnarrowed it offered twenty-three types under a family that can match at most three of
            them, and picking one of the other twenty emptied the page with no hint that Family was
            the reason.
          </>,
        ),
        note(
          <>
            A type already selected survives the narrowing even when it belongs to another family,
            because that URL was shareable before the narrowing existed and links outlive dropdowns.
            Without the exception the filter is on with nothing on screen naming it, and there is no
            row to untick to escape.
          </>,
        ),
        p(
          <>
            Five families are filterable: rank, technical, event, intraday, and swing.{" "}
            <Datum>event</Datum> holds one type today, the ex-date avoid flag, so filtering to it
            lists every bonus and split still inside its {QUOTED.EX_DATE_AVOID_SESSIONS}-session
            window.
          </>,
        ),
      ],
    },
    {
      heading: "The three presets",
      blocks: [
        p(
          <>
            The Type dropdown holds thirty-five entries across seven groups, and the three questions
            a reader asks it most are not groups. Each preset is a fixed list they would otherwise
            tick one at a time on every load.
          </>,
        ),
        table(
          "One-click type presets",
          ["Preset", "Selects"],
          [
            ["Prophet", <Datum key="p">prophet_swing_entry, prophet_swing_exit</Datum>],
            [
              "ICT",
              <Datum key="i">
                confluence_bullish_strong, confluence_bullish, confluence_bearish,
                confluence_bearish_strong, ict_long, ict_short
              </Datum>,
            ],
            ["Breakout", <Datum key="b">volume_breakout_bullish, volume_breakout_bearish</Datum>],
          ],
        ),
        p(
          <>
            The ICT preset picks the six types a trade is taken on rather than all ten ICT
            primitives. <Datum>ict_ob_*</Datum>, <Datum>ict_fvg_*</Datum>, and{" "}
            <Datum>ict_sweep_*</Datum> fire on most symbol-sessions and would bury the six.
          </>,
        ),
        note(
          <>
            Presets are additive and light up when every type they name is ticked, so Prophet and
            Breakout compose into one question. A click also clears Family, because two of the three
            span families: <Datum>ict_long</Datum> is a swing row and{" "}
            <Datum>confluence_bullish</Datum> is an intraday one.
          </>,
        ),
      ],
    },
    {
      heading: "Side is derived, and a fifth of the feed has none",
      code: "?side=",
      blocks: [
        p(
          <>
            No row stores a direction. It is read off the payload when the emitter wrote one, and
            off the type name otherwise — <Datum>*_bullish</Datum> is long, <Datum>*_bearish</Datum>{" "}
            is short, <Datum>high_52w_on_volume</Datum> is long and <Datum>low_52w_on_volume</Datum>{" "}
            is short.
          </>,
        ),
        p(
          <>
            The payload wins, and it is not a tie-break. A <Datum>gap_up</Datum> is a{" "}
            <strong>short</strong>: the strategy fades the gap. Anything reading direction off that
            type name is exactly backwards, on 84 rows in a five-session window.
          </>,
        ),
        table(
          "The four chips",
          ["Chip", "Shows"],
          [
            ["Any", "Every row, including the ones with no direction"],
            ["Long", <Datum key="l">?side=long</Datum>],
            ["Short", <Datum key="s">?side=short</Datum>],
            ["No side", <Datum key="n">?side=none</Datum>],
          ],
        ),
        note(
          <>
            "Any" rather than "Both", because long and short do not cover the feed. Rank decile
            crossings and series changes name no direction at all — 1,411 of 6,766 rows over a
            five-session window — so picking Long or Short drops all of them. That is the point of
            picking a side, but it is a large drop and worth knowing before reading the count.
          </>,
        ),
      ],
    },
    {
      heading: "Confluence rows double-count",
      blocks: [
        p(
          <>
            Confluence is scored from the votes of strategies that each wrote their own row a moment
            earlier, and it needs at least <Datum>{QUOTED.CONFLUENCE_FLOOR}</Datum> of them to
            agree. So on a bucket where three strategies agree, the feed carries five rows for three
            observations.
          </>,
        ),
        p(
          <>
            Every count taken over the feed overstates by that ratio, and the summary line is the
            one a reader trusts before they have read a single row. Drop the confluence types before
            comparing one day's count against another's.
          </>,
        ),
      ],
    },
    {
      heading: "When a family reads zero",
      blocks: [
        p(<>Almost never a broken emitter. Check these four before you open the worker.</>),
        ul([
          <>
            The shared session window. The default covers the current trading day and the one before
            it, and the window is the newest session dates across every family at once. Intraday
            writes several times a session and the daily job writes once after the close, so one
            missed daily run leaves intraday owning both dates while rank and technical read empty.
          </>,
          <>
            A stale type in the URL. A family-plus-type pair that cannot coexist returns nothing,
            and a bookmark keeps the pair alive long after the reader has forgotten it.
          </>,
          <>
            A strategy switched off on the settings page. It hides every type that strategy owns, so
            the rows stop appearing while the emitter is fine.
          </>,
          <>
            A severity floor the family never reaches. Every rank signal is forced to{" "}
            <Datum>info</Datum>, so a rank filter with a warn floor is structurally empty.
          </>,
        ]),
      ],
    },
    {
      heading: "Deliveries: filtered is the normal outcome",
      blocks: [
        p(
          <>
            On the deliveries view, <Datum>0 delivered of 431</Datum> is the severity preference
            doing its job. A signal no notification rule matches is marked <Datum>filtered</Datum>{" "}
            so it stops coming back on every run forever, and that is most of them. Prod marked{" "}
            <Datum>1,596</Datum> of <Datum>1,624</Datum> rows filtered on <Datum>2026-09-02</Datum>.
            Roughly <Datum>25 to 45</Datum> signals a day go out.
          </>,
        ),
        note(
          <>
            A real outage looks different. It shows a reason that is not <Datum>filtered</Datum>,
            usually a Telegram API error string, or <Datum>signal-delivery</Datum> runs going red on
            the Health page.
          </>,
        ),
      ],
    },
  ],
};
